import { useEffect } from 'react';
import { isAnalyticsAllowed, useConsentStore } from '@/lib/consent/store';
import type { AnalyticsEventMap, AnalyticsEventName } from './events';
import type * as RuntimeModule from './runtime';

export type { AnalyticsEventMap, AnalyticsEventName } from './events';

/**
 * The entry-side half of analytics: a consent listener and `track()`. It is tiny on purpose — the
 * provider logic, the page-view hooks and the vendors' code live in `./runtime`, a separate chunk that
 * is requested only when
 *   (1) a provider is configured at build time (`VITE_ANALYTICS_PROVIDER`), and
 *   (2) the visitor has pressed Accept.
 * Without both, nothing is downloaded, nothing is sent and `track()` returns immediately.
 */

type Runtime = typeof RuntimeModule;

let runtime: Runtime | undefined;
let loading: Promise<Runtime> | undefined;

/** A provider was chosen in the build. Read when asked, so a test can stub the environment. */
const isConfigured = (): boolean => Boolean(import.meta.env.VITE_ANALYTICS_PROVIDER?.trim());

function loadRuntime(): Promise<Runtime> {
  loading ??= import('./runtime').then(
    (module) => {
      runtime = module;
      return module;
    },
    (error: unknown) => {
      loading = undefined; // a later Accept or event may try again (offline, a new deploy)
      throw error;
    },
  );
  return loading;
}

/** Tell the runtime what the visitor has decided. Synchronous once the runtime is loaded: withdrawal is immediate. */
function syncConsent(): void {
  const allowed = isAnalyticsAllowed();
  if (runtime) {
    runtime.setConsent(allowed);
    return;
  }
  if (!allowed) return; // never loaded, nothing to stop
  loadRuntime().then(
    (module) => {
      // the decision may have changed while the chunk was downloading — ask again rather than trust a stale one
      module.setConsent(isAnalyticsAllowed());
    },
    () => undefined,
  );
}

/** Follow the consent store for the life of the page. Returns the unsubscribe function. */
export function startAnalytics(): () => void {
  if (!isConfigured()) return () => undefined;
  syncConsent();
  return useConsentStore.subscribe((state, previous) => {
    if (state.decision !== previous.decision) syncConsent();
  });
}

/** Mount once, in the providers. */
export function useAnalyticsBootstrap(): void {
  useEffect(() => startAnalytics(), []);
}

/**
 * Record one event from the closed list (`./events`). A no-op unless analytics is configured AND the
 * visitor has accepted it; never throws; the properties are validated again before anything is sent.
 */
export function track<N extends AnalyticsEventName>(name: N, props?: AnalyticsEventMap[N]): void {
  if (!isConfigured() || !isAnalyticsAllowed()) return;
  if (runtime) {
    runtime.trackEvent(name, props);
    return;
  }
  loadRuntime().then(
    (module) => {
      module.trackEvent(name, props);
    },
    () => undefined,
  );
}
